import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductGalleryComponent } from './product-gallery.component';
import { ProductBuyOptionsComponent } from './product-buy-options';
import { ProductDescriptionTabsComponent } from './product-description-tabs.component';
import { ProductRelatedSectionComponent } from './product-related-section.component';
import { ProductDetail, ProductAddon, AddToCartPayload, ProductStore } from '../../core/models/interfaces-general';
import { ProductoService } from '../../core/services/producto.service';
import { ProductoMapper } from '../../core/mappers/producto.mapper';
import { Constante } from '../../constante';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

@Component({
    selector: 'app-product-detail-page',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        ProductGalleryComponent,
        ProductBuyOptionsComponent,
        ProductDescriptionTabsComponent,
        ProductRelatedSectionComponent,
        UiLoadingComponent
    ],
    template: `
    <app-ui-loading *ngIf="!product()"></app-ui-loading>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6 flex flex-col gap-10" *ngIf="product()">
      
      <nav class="text-xs text-gray-400 font-medium flex items-center gap-2">
        <a routerLink="/" class="hover:text-rose-500">Inicio</a> / 
        <a routerLink="/catalogo" class="hover:text-rose-500">Catálogo</a> / 
        <span class="text-gray-600 font-semibold">{{ product()!.title }}</span>
      </nav>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        <app-product-gallery 
          [images]="product()!.images" 
          [altText]="product()!.title"
        ></app-product-gallery>

        <app-product-buy-options 
          [product]="product()!"
          [availableAddons]="addons()"
          (buy)="onAddToCart($event)"
        ></app-product-buy-options>
      </div>

      <app-product-description-tabs 
        [title]="product()!.title"
        [description]="product()!.descriptionHtml || ''"
        [specifications]="product()!.specifications || []"
      ></app-product-description-tabs>

      <app-product-related-section 
        [relatedProducts]="relatedProducts()"
      ></app-product-related-section>

    </main>
  `
})
export class ProductDetailPageComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private productoService = inject(ProductoService);

    product = signal<ProductDetail | null>(null);
    addons = signal<ProductAddon[]>([]);
    relatedProducts = signal<ProductStore[]>([]);

    ngOnInit() {
        this.route.paramMap.subscribe((params) => {
            const slug = params.get('slug');
            if (!slug) {
                this.router.navigate([Constante.URL_CATALOGO]);
                return;
            }
            this.load(slug);
        });
    }

    onAddToCart(_payload: AddToCartPayload) {
        this.router.navigate([Constante.URL_CHECKOUT]);
    }

    private load(slug: string) {
        this.productoService.getProductoBySlug(slug).subscribe({
            next: (detalle) => {
                if (!detalle) {
                    this.router.navigate([Constante.URL_CATALOGO]);
                    return;
                }
                this.product.set(ProductoMapper.toDetail(detalle));
                this.addons.set((detalle.complementos ?? []).map((item) => ProductoMapper.toAddon(item)));
                this.relatedProducts.set((detalle.relacionados ?? []).map((item) => ProductoMapper.toStore(item)));
            },
            error: () => {
                this.router.navigate([Constante.URL_CATALOGO]);
            }
        });
    }
}
